import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getListState, setListState } from "../../lib/listState";

/**
 * Clean, consistent CRM pagination hook.
 * Handles automatic clamping, page slicing, and state management.
 * Supports persistent section state across unmounts/navigation.
 */
export function usePagination(items = [], defaultPageSize = 10, options = {}) {
  // Support options as string (sectionKey) or number (initialPage) or object
  const sectionKey = typeof options === "string" ? options : (options?.sectionKey || null);
  const initialPage = typeof options === "number"
    ? options
    : (options?.initialPage || (sectionKey ? getListState(sectionKey, {}).currentPage || 1 : 1));
  const savedPageSize = sectionKey
    ? getListState(sectionKey, {}).pageSize || defaultPageSize
    : (options?.pageSize || defaultPageSize);

  const [currentPage, setCurrentPageRaw] = useState(initialPage);
  const [pageSize, setPageSizeRaw] = useState(savedPageSize);

  const setCurrentPage = useCallback((updater) => {
    setCurrentPageRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (sectionKey) {
        setListState(sectionKey, { currentPage: next });
      }
      if (typeof options === "object" && options?.onPageChange) {
        options.onPageChange(next);
      }
      return next;
    });
  }, [sectionKey, options]);

  const setPageSize = useCallback((updater) => {
    setPageSizeRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (sectionKey) {
        setListState(sectionKey, { pageSize: next });
      }
      if (typeof options === "object" && options?.onPageSizeChange) {
        options.onPageSizeChange(next);
      }
      return next;
    });
  }, [sectionKey, options]);

  const totalItems = (items || []).length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Auto-clamp page if items reduce (e.g. filter applied),
  // but ONLY when totalItems > 0 so we don't prematurely reset currentPage during initial async data loading!
  useEffect(() => {
    if (totalItems > 0) {
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      } else if (currentPage < 1) {
        setCurrentPage(1);
      }
    }
  }, [currentPage, totalPages, totalItems, setCurrentPage]);

  const paginatedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    const safePage = Math.min(Math.max(1, currentPage), totalPages);
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, currentPage, pageSize, totalPages]);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, [setCurrentPage]);

  return {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalItems,
    paginatedItems,
    resetPage,
  };
}

/**
 * Reusable Pagination Controls Bar for all CRM tables & lists.
 */
export default function Pagination({
  currentPage = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange,
  itemLabel = "records",
  pageSizeOptions = [10, 25, 50],
  onPageSizeChange,
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const startItem = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (safePage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }

    if (safePage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [1, "...", safePage - 1, safePage, safePage + 1, "...", totalPages];
  };

  const pages = getPageNumbers();

  const handlePrev = () => {
    if (safePage > 1 && onPageChange) {
      onPageChange(safePage - 1);
    }
  };

  const handleNext = () => {
    if (safePage < totalPages && onPageChange) {
      onPageChange(safePage + 1);
    }
  };

  return (
    <div
      className="crm-pagination"
      data-testid="crm-pagination-container"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        padding: "12px 18px",
        background: "#ffffff",
        borderTop: "1px solid #eef2f7",
        fontSize: 12.5,
      }}
    >
      {/* Left: Range and Total Count summary */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: "#475569",
          fontWeight: 500,
        }}
        data-testid="pagination-summary-info"
      >
        <span>
          Showing{" "}
          <strong style={{ color: "var(--navy, #0f172a)", fontWeight: 700 }}>
            {startItem}–{endItem}
          </strong>{" "}
          of{" "}
          <strong style={{ color: "var(--navy, #0f172a)", fontWeight: 700 }}>
            {totalItems}
          </strong>{" "}
          {itemLabel}
        </span>

        {onPageSizeChange && pageSizeOptions && pageSizeOptions.length > 1 && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginLeft: 8 }}>
            <span style={{ fontSize: 11.5, color: "#64748b" }}>Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                onPageSizeChange(newSize);
                if (onPageChange) onPageChange(1);
              }}
              style={{
                height: 26,
                padding: "0 6px",
                fontSize: 11.5,
                borderRadius: 4,
                border: "1px solid #cbd5e1",
                background: "#fff",
              }}
              data-testid="pagination-pagesize-select"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right: Page Navigation Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
        data-testid="pagination-controls"
      >
        {/* Previous Button */}
        <button
          type="button"
          onClick={handlePrev}
          disabled={safePage <= 1 || totalItems === 0}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "5px 10px",
            height: 30,
            borderRadius: 5,
            border: "1px solid #cbd5e1",
            background: safePage <= 1 ? "#f8fafc" : "#ffffff",
            color: safePage <= 1 ? "#94a3b8" : "#1e293b",
            fontSize: 12,
            fontWeight: 600,
            cursor: safePage <= 1 ? "not-allowed" : "pointer",
            transition: "all 0.15s ease",
            opacity: safePage <= 1 ? 0.6 : 1,
          }}
          data-testid="pagination-prev-btn"
          title="Previous Page"
        >
          <ChevronLeft size={14} />
          <span>Previous</span>
        </button>

        {/* Numbered Page Buttons */}
        <div style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
          {pages.map((p, idx) => {
            if (p === "...") {
              return (
                <span
                  key={`ellipsis-${idx}`}
                  style={{
                    padding: "0 4px",
                    color: "#94a3b8",
                    fontSize: 12,
                    userSelect: "none",
                  }}
                >
                  …
                </span>
              );
            }

            const isActive = p === safePage;
            return (
              <button
                key={`page-${p}`}
                type="button"
                onClick={() => onPageChange && onPageChange(p)}
                style={{
                  minWidth: 28,
                  height: 30,
                  padding: "0 6px",
                  borderRadius: 5,
                  border: isActive ? "1px solid var(--navy, #0f172a)" : "1px solid #cbd5e1",
                  background: isActive ? "var(--navy, #0f172a)" : "#ffffff",
                  color: isActive ? "#ffffff" : "#334155",
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  cursor: isActive ? "default" : "pointer",
                  transition: "all 0.15s ease",
                }}
                data-testid={`pagination-page-${p}`}
                aria-current={isActive ? "page" : undefined}
              >
                {p}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          type="button"
          onClick={handleNext}
          disabled={safePage >= totalPages || totalItems === 0}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "5px 10px",
            height: 30,
            borderRadius: 5,
            border: "1px solid #cbd5e1",
            background: safePage >= totalPages || totalItems === 0 ? "#f8fafc" : "#ffffff",
            color: safePage >= totalPages || totalItems === 0 ? "#94a3b8" : "#1e293b",
            fontSize: 12,
            fontWeight: 600,
            cursor: safePage >= totalPages || totalItems === 0 ? "not-allowed" : "pointer",
            transition: "all 0.15s ease",
            opacity: safePage >= totalPages || totalItems === 0 ? 0.6 : 1,
          }}
          data-testid="pagination-next-btn"
          title="Next Page"
        >
          <span>Next</span>
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
